import { useCallback, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { extractFunctionErrorMessage } from '@/features/financials/hooks';
import type { QbPostStatus } from '@/types/database';

/**
 * Bulk-post orchestrator for the Live Transactions grid.
 *
 * The per-row `qbo-post-transaction` Edge Function has always worked one row at
 * a time. The previous "Post N to QBO" button packed every selected id into a
 * single invocation: the operator clicked, watched a generic spinner for 30s,
 * and then got a single all-or-nothing toast at the end. That hid the
 * in-flight state (which row is going next?), gave the user no way to know
 * whether QBO was rate-limiting, and made retries an all-or-nothing exercise.
 *
 * This hook fixes the operator UX for the bulk path without changing the
 * Edge Function:
 *
 *   • Runs CONCURRENCY=3 workers across the id list rather than fanning out
 *     all N posts in parallel. That dodges QBO's per-token rate limit (and
 *     stops the server-side classifier from queueing up) while still being
 *     ~3× faster than strictly serial. The function is already idempotent
 *     on `qb_external_id` so the bounded window doubles as a safety net.
 *
 *   • Maintains a per-row state map (`queued` / `posting` / `posted` /
 *     `failed` / `skipped`) the grid overlays on its QBO Status column so
 *     the operator can watch the batch progress without page-level refetches.
 *
 *   • Pumps a single persistent Sonner toast (`Posting X of N…`), so a
 *     30-row batch produces one piece of chrome instead of 30. The toast
 *     swaps in place to a summary on completion (`Posted N · Failed M`)
 *     with an optional `Show failures` action that flips the page's filter.
 *
 *   • Patches the React Query cache per row as posts complete instead of
 *     invalidating between every one. A full invalidation runs once at the
 *     end as a belt-and-suspenders refresh — catches trigger-driven side
 *     effects we didn't mirror locally (e.g. transfer follower flips).
 *
 * Transfer followers are deliberately NOT excluded inside this hook: the
 * existing `postable` filter on the page already drops them (they sit at
 * `expense_status='in_qb'`), so by the time ids arrive here every entry is
 * a row we actually want to post.
 */

const CONCURRENCY = 3;
const TOAST_ID = 'bulk-post-qbo';

export type BulkPostRowState =
  | { status: 'queued' }
  | { status: 'posting' }
  | {
      status: 'posted';
      recreated: boolean;
      attachment?:
        | 'uploaded'
        | 'replaced'
        | 'removed'
        | 'unchanged'
        | 'none'
        | 'failed';
    }
  | { status: 'failed'; error: string }
  | { status: 'skipped'; reason: string };

interface BatchResult {
  posted: number;
  failed: number;
  skipped: number;
  recreated: number;
  attachmentsUploaded: number;
  attachmentsFailed: number;
  failedIds: string[];
}

type SingleRowResult = {
  id: string;
  status: 'posted' | 'failed' | 'skipped';
  error?: string;
  recreated?: boolean;
  attachment?:
    | 'uploaded'
    | 'replaced'
    | 'removed'
    | 'unchanged'
    | 'none'
    | 'failed';
  attachment_error?: string;
};

interface InvokeResp {
  ok: boolean;
  posted: number;
  failed: number;
  results: SingleRowResult[];
  error?: string;
}

export interface UseBulkPostToQboOptions {
  /**
   * Invoked from the summary toast's `Show failures` action button. The
   * page is expected to flip into a "show only these ids" view so the
   * operator can correct + retry without scrolling 100 rows of green
   * Posted pills to find the 3 reds.
   */
  onShowFailures?: (ids: string[]) => void;
}

export function useBulkPostToQbo(options?: UseBulkPostToQboOptions) {
  const qc = useQueryClient();
  const [rowStates, setRowStates] = useState<Map<string, BulkPostRowState>>(
    () => new Map(),
  );
  const [isRunning, setIsRunning] = useState(false);
  // Reentrancy ref: a double-click between `setIsRunning(true)` and the
  // next render would otherwise see the stale `isRunning=false` closure
  // and kick off a second batch. The disabled button covers the common
  // case; this ref covers the millisecond race.
  const runningRef = useRef(false);
  // Counters live in a ref so the worker loop can read/write them without
  // re-triggering the closure capture dance every render. The toast pulls
  // its `X of N` text off these on every update.
  const counterRef = useRef({ done: 0, total: 0 });

  /**
   * Patch a single transaction row across every cached `useTransactions(...)`
   * list (and the single-row detail view, if open). Mutates the raw rows
   * the queryFn returned — `useTransactions` runs its transformations in
   * `select`, so the patched fields flow through on the next render.
   */
  const patchCachedRow = useCallback(
    (
      id: string,
      patch: Partial<{
        qb_post_status: QbPostStatus;
        qb_last_error: string | null;
      }>,
    ) => {
      const lists = qc.getQueriesData<unknown[]>({ queryKey: ['transactions'] });
      for (const [key, list] of lists) {
        if (!Array.isArray(list)) continue;
        let mutated = false;
        const next = list.map((row) => {
          const r = row as Record<string, unknown>;
          if (r.id !== id) return row;
          mutated = true;
          return { ...r, ...patch };
        });
        if (mutated) qc.setQueryData(key, next);
      }
      const detailKey = ['transaction', id] as const;
      const prev = qc.getQueryData<Record<string, unknown>>(detailKey);
      if (prev) {
        qc.setQueryData(detailKey, { ...prev, ...patch });
      }
    },
    [qc],
  );

  const setRowState = useCallback((id: string, state: BulkPostRowState) => {
    setRowStates((prev) => {
      const next = new Map(prev);
      next.set(id, state);
      return next;
    });
  }, []);

  const updateProgressToast = useCallback(() => {
    const { done, total } = counterRef.current;
    if (total === 0) return;
    const inFlight = Math.min(done + 1, total);
    toast.loading(`Posting ${inFlight} of ${total} to QuickBooks…`, {
      id: TOAST_ID,
      description: `${done} done so far`,
      // Sonner's loading toast persists until the same id is reissued
      // with .success / .warning, but be explicit so a stray default
      // duration can't dismiss the progress chrome mid-batch.
      duration: Number.POSITIVE_INFINITY,
    });
  }, []);

  const postOne = useCallback(
    async (id: string): Promise<SingleRowResult> => {
      setRowState(id, { status: 'posting' });
      patchCachedRow(id, { qb_post_status: 'posting' });
      try {
        const { data, error } = await supabase.functions.invoke<InvokeResp>(
          'qbo-post-transaction',
          { body: { transaction_ids: [id] } },
        );
        if (error) {
          throw new Error(await extractFunctionErrorMessage(error));
        }
        if (!data?.ok) {
          throw new Error(data?.error ?? 'Post failed');
        }
        const r = data.results.find((x) => x.id === id);
        if (!r) {
          throw new Error('Edge Function returned no result for this row');
        }
        return r;
      } catch (e) {
        return {
          id,
          status: 'failed',
          error: e instanceof Error ? e.message : 'Post failed',
        };
      }
    },
    [patchCachedRow, setRowState],
  );

  const start = useCallback(
    async (ids: string[]): Promise<BatchResult | null> => {
      if (runningRef.current) {
        toast.warning('Another bulk post is still running.');
        return null;
      }
      if (ids.length === 0) return null;

      runningRef.current = true;
      setIsRunning(true);
      counterRef.current = { done: 0, total: ids.length };

      // Seed the local state map up front so every selected row flips to
      // "Queued" the moment the operator clicks — even rows a slow worker
      // hasn't picked up yet. Without this, only the first CONCURRENCY
      // rows would show any feedback for the first ~1s.
      const seed = new Map<string, BulkPostRowState>();
      for (const id of ids) seed.set(id, { status: 'queued' });
      setRowStates(seed);

      toast.loading(`Posting 1 of ${ids.length} to QuickBooks…`, {
        id: TOAST_ID,
        description: '0 done so far',
        duration: Number.POSITIVE_INFINITY,
      });

      const result: BatchResult = {
        posted: 0,
        failed: 0,
        skipped: 0,
        recreated: 0,
        attachmentsUploaded: 0,
        attachmentsFailed: 0,
        failedIds: [],
      };

      // Simple work-stealing pool: kick off CONCURRENCY workers, each one
      // pulls the next id off a shared cursor until the queue is drained.
      // Using a cursor (rather than splitting into K static chunks)
      // matters because per-row latency varies wildly — a transaction
      // that needs a CoA re-resolve, a vendor create, and a receipt
      // upload can take 5× as long as a straight Purchase. Work-steal
      // means a fast worker never sits idle while a slow one finishes.
      let cursor = 0;
      const next = (): string | undefined =>
        cursor < ids.length ? ids[cursor++] : undefined;

      const worker = async (): Promise<void> => {
        let id = next();
        while (id !== undefined) {
          updateProgressToast();
          const r = await postOne(id);

          if (r.status === 'posted') {
            result.posted += 1;
            if (r.recreated) result.recreated += 1;
            if (r.attachment === 'uploaded' || r.attachment === 'replaced') {
              result.attachmentsUploaded += 1;
            }
            if (r.attachment === 'failed') result.attachmentsFailed += 1;
            setRowState(id, {
              status: 'posted',
              recreated: !!r.recreated,
              attachment: r.attachment,
            });
            patchCachedRow(id, {
              qb_post_status: 'posted',
              qb_last_error: null,
            });
          } else if (r.status === 'skipped') {
            result.skipped += 1;
            setRowState(id, {
              status: 'skipped',
              reason: r.error ?? 'Skipped server-side',
            });
            // Deliberately don't touch the cache — the row's real status
            // is whatever the trigger pinned (e.g. 'skipped' for
            // bank-failed rows or rows the operator marked 'in_qb').
          } else {
            result.failed += 1;
            result.failedIds.push(id);
            const msg = r.error ?? 'Post failed';
            setRowState(id, { status: 'failed', error: msg });
            patchCachedRow(id, {
              qb_post_status: 'failed',
              qb_last_error: msg,
            });
          }

          counterRef.current.done += 1;
          updateProgressToast();
          id = next();
        }
      };

      const workers = Array.from(
        { length: Math.min(CONCURRENCY, ids.length) },
        () => worker(),
      );
      await Promise.all(workers);

      // Belt-and-suspenders refresh. Per-row cache patches above keep the
      // grid live during the batch; this catches anything the server
      // mutated that we didn't mirror (paired transfer follower flips,
      // expense_status changes the post-trigger applied, recon view, etc).
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['transactions'] }),
        qc.invalidateQueries({ queryKey: ['transaction'] }),
        qc.invalidateQueries({ queryKey: ['expense-transactions'] }),
        qc.invalidateQueries({ queryKey: ['bank-reconciliation'] }),
      ]);

      runningRef.current = false;
      setIsRunning(false);

      // Final summary — replaces the in-flight loading toast in place
      // (same TOAST_ID) so the operator's eye stays on the same UI element
      // through the transition.
      const parts: string[] = [`Posted ${result.posted}`];
      if (result.failed > 0) parts.push(`${result.failed} failed`);
      if (result.skipped > 0) parts.push(`${result.skipped} skipped`);
      const headline = parts.join(' · ');

      const description =
        result.recreated > 0
          ? `${result.recreated} were missing in QuickBooks and were re-created fresh.`
          : undefined;

      if (result.failed > 0) {
        toast.warning(headline, {
          id: TOAST_ID,
          description,
          duration: 10_000,
          action: options?.onShowFailures
            ? {
                label: 'Show failures',
                onClick: () => options.onShowFailures?.(result.failedIds),
              }
            : undefined,
        });
      } else if (result.posted > 0) {
        toast.success(headline, {
          id: TOAST_ID,
          description,
          duration: 6_000,
        });
      } else {
        toast.info('Nothing to post.', { id: TOAST_ID, duration: 4_000 });
      }

      // Receipt mirroring is a separate non-fatal step on the server side;
      // roll up the per-row outcomes the same way the single-shot path
      // does so the operator sees one extra chip rather than N.
      if (result.attachmentsFailed > 0) {
        toast.warning(
          `${result.attachmentsFailed} receipt${result.attachmentsFailed === 1 ? '' : 's'} couldn't be attached in QuickBooks. The next post will retry the upload.`,
          { duration: 8000 },
        );
      } else if (result.attachmentsUploaded > 0) {
        toast.success(
          `Attached ${result.attachmentsUploaded} receipt${result.attachmentsUploaded === 1 ? '' : 's'} in QuickBooks.`,
        );
      }

      return result;
    },
    // `isRunning` deliberately NOT in deps — the reentrancy guard reads
    // `runningRef.current`, which is always current without forcing the
    // callback to rebuild every time the state toggles. Keeping `start`
    // stable across the run prevents column memos in callers (which
    // depend on `bulkPost.start` transitively) from churning mid-batch.
    [options, patchCachedRow, postOne, qc, setRowState, updateProgressToast],
  );

  /** Clear the per-row overlay state — used when the operator dismisses
   *  the "Show failures" filter, so retrying a row removes the stale
   *  "failed" pill that the cache may have already updated past. */
  const reset = useCallback(() => {
    setRowStates(new Map());
  }, []);

  const getRowState = useCallback(
    (id: string): BulkPostRowState | undefined => rowStates.get(id),
    [rowStates],
  );

  return useMemo(
    () => ({
      start,
      isRunning,
      getRowState,
      reset,
      rowStates,
    }),
    [start, isRunning, getRowState, reset, rowStates],
  );
}

export type BulkPostController = ReturnType<typeof useBulkPostToQbo>;
